import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  CheckCircle2,
  Pencil,
  XCircle,
  FileQuestion,
  SplitSquareVertical,
  Sparkles,
  Download,
  AlertTriangle,
  ExternalLink,
  ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import TestimonyShareInline from './TestimonyShareInline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OPTIONS = [
  {
    id: 'deleted',
    label: 'Item(s) deleted / removed',
    description: 'The bureau agreed — the disputed item is gone from my report.',
    icon: CheckCircle2,
    tone: 'success',
  },
  {
    id: 'updated',
    label: 'Item(s) updated / corrected',
    description: 'The balance, date, or status was fixed.',
    icon: Pencil,
    tone: 'success',
  },
  {
    id: 'verified',
    label: 'Verified as accurate (unchanged)',
    description: "The bureau says the item stays. Here's what to do next.",
    icon: XCircle,
    tone: 'danger',
  },
  {
    id: 'needs_info',
    label: 'Needs more information / documents',
    description: 'The bureau wants ID, proof of address, or something else.',
    icon: FileQuestion,
    tone: 'warning',
  },
  {
    id: 'partial',
    label: 'Partial — some removed, some verified',
    description: 'Mixed result. Plan next steps for items still on the report.',
    icon: SplitSquareVertical,
    tone: 'warning',
  },
];

const TONE = {
  success: 'border-[#4CAF50]/40 hover:border-[#4CAF50] bg-[#4CAF50]/5',
  danger: 'border-[#D9534F]/40 hover:border-[#D9534F] bg-[#D9534F]/5',
  warning: 'border-[#B76E79]/40 hover:border-[#B76E79] bg-[#E0BFB8]/10',
};

const TONE_ICON = {
  success: 'text-[#4CAF50]',
  danger: 'text-[#D9534F]',
  warning: 'text-[#B76E79]',
};

// Compact select options for per-item mode (no 'partial' — partial is an aggregate state, not per-item)
const PER_ITEM_OPTIONS = OPTIONS.filter((o) => o.id !== 'partial');

export default function ResponseReceivedDialog({ open, onOpenChange, dispute, onComplete, onResolved }) {
  const items = dispute?.items || [];
  const multiItem = items.length > 1;

  const [step, setStep] = useState('select'); // select | guidance
  const [selected, setSelected] = useState(null); // single-item mode
  const [perItem, setPerItem] = useState({}); // { [itemIndex]: response_type }
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [movDownloading, setMovDownloading] = useState(false);
  const [testimonyDone, setTestimonyDone] = useState(false);

  const reset = () => {
    setStep('select');
    setSelected(null);
    setPerItem({});
    setNotes('');
    setResult(null);
    setTestimonyDone(false);
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const canSubmit = multiItem
    ? items.every((_, i) => perItem[i])
    : !!selected;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = multiItem
        ? {
            item_responses: items.map((_, i) => ({ item_index: i, response_type: perItem[i] })),
            notes: notes.trim() || null,
          }
        : { response_type: selected, notes: notes.trim() || null };

      const { data } = await axios.post(
        `${API_URL}/api/dispute/${dispute.dispute_id}/record-response`,
        payload,
        { withCredentials: true }
      );
      setResult(data);
      setStep('guidance');
      if (data.auto_resolved) {
        toast.success('Your slate is clearing!', {
          description: data.label,
        });
        onResolved && onResolved({ ...data, bureau: dispute?.bureau, items });
      } else {
        toast.success('Response recorded', { description: data.label });
      }
      onComplete && onComplete();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateMov = async () => {
    setMovDownloading(true);
    try {
      await axios.post(
        `${API_URL}/api/dispute/${dispute.dispute_id}/mov-letter`,
        {},
        { withCredentials: true }
      );
      const token = localStorage.getItem('selah_access_token');
      window.location.href = `${API_URL}/api/dispute/${dispute.dispute_id}/mov-letter/download?token=${token}`;
      toast.success('MOV letter ready', { description: 'Downloading…' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate MOV letter');
    } finally {
      setMovDownloading(false);
    }
  };

  // Guidance flags
  const r = result || {};
  // Per-item mode: check individual responses; single mode: use response_type
  const hasVerified = multiItem
    ? (r.item_responses || []).some((x) => x.response_type === 'verified')
    : r.response_type === 'verified';
  const hasNeedsInfo = multiItem
    ? (r.item_responses || []).some((x) => x.response_type === 'needs_info')
    : r.response_type === 'needs_info';
  const anyGood = !!r.any_good_result;
  const showMovAction = hasVerified;
  const showCfpbAction = hasVerified;
  const showDocsChecklist = hasNeedsInfo && !hasVerified;
  const showTestimony = anyGood && !testimonyDone;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="response-received-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Playfair_Display'] text-2xl text-[#36454F]">
            {step === 'select'
              ? (multiItem ? 'Record each item\'s response' : 'What did the bureau say?')
              : (r.label || 'Response recorded')}
          </DialogTitle>
          <DialogDescription className="font-['Manrope'] text-[#36454F]/65">
            {step === 'select'
              ? (multiItem
                  ? `This dispute has ${items.length} items. Mark the bureau's response for each — the dispute is only marked resolved when every item is removed or updated.`
                  : `Response for ${dispute?.bureau || 'bureau'} dispute. This guides your next step.`)
              : r.auto_resolved
                ? 'All items resolved! Here are a couple of follow-ups to protect your win.'
                : anyGood
                  ? 'Some items cleared, others still need action. Keep going.'
                  : "Here's what we recommend next."}
          </DialogDescription>
        </DialogHeader>

        {step === 'select' && !multiItem && (
          <SingleItemSelect
            options={OPTIONS}
            selected={selected}
            onSelect={setSelected}
            notes={notes}
            onNotes={setNotes}
          />
        )}

        {step === 'select' && multiItem && (
          <PerItemSelect
            items={items}
            perItem={perItem}
            onChange={(idx, val) => setPerItem((prev) => ({ ...prev, [idx]: val }))}
            notes={notes}
            onNotes={setNotes}
          />
        )}

        {step === 'select' && (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#36454F]/10 mt-2">
            {multiItem && (
              <p className="text-xs text-[#36454F]/50 font-['Manrope']">
                {Object.keys(perItem).length} of {items.length} answered
              </p>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => handleOpenChange(false)}
                className="text-[#36454F]/60 font-['Manrope']"
                data-testid="response-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
                data-testid="response-submit-btn"
              >
                {submitting ? 'Recording…' : 'Record Response'}
              </Button>
            </div>
          </div>
        )}

        {step === 'guidance' && result && (
          <div className="space-y-4">
            {/* Banner */}
            {r.auto_resolved ? (
              <div className="p-4 bg-[#4CAF50]/10 border border-[#4CAF50]/30 rounded-sm flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-[#4CAF50] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="font-['Manrope'] font-medium text-[#36454F]">Your slate is clearing!</p>
                  <p className="text-sm text-[#36454F]/65 font-['Manrope'] mt-0.5">
                    "See, I am doing a new thing!" — Isaiah 43:19
                  </p>
                </div>
              </div>
            ) : anyGood ? (
              <div className="p-4 bg-[#E0BFB8]/15 border border-[#B76E79]/30 rounded-sm flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-[#B76E79] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="font-['Manrope'] font-medium text-[#36454F]">
                    {r.resolved_count} of {(r.resolved_count ?? 0) + (r.pending_count ?? 0)} items cleared
                  </p>
                  <p className="text-sm text-[#36454F]/65 font-['Manrope'] mt-0.5">
                    Don't stop here — here's how to handle the items still on your report.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-[#E0BFB8]/15 border border-[#B76E79]/30 rounded-sm flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#B76E79] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-sm text-[#36454F]/80 font-['Manrope']">
                  You still have options. Don't let the first response be the final word.
                </p>
              </div>
            )}

            {/* Per-item result summary (only for multi-item) */}
            {multiItem && r.item_responses?.length > 0 && (
              <div className="space-y-1.5 pt-1">
                <p className="text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope']">
                  Item-by-item outcome
                </p>
                {r.item_responses.map((ir) => (
                  <div
                    key={ir.item_index}
                    className="flex items-start justify-between gap-3 p-2.5 rounded-sm bg-[#F5F5F5] text-sm"
                    data-testid={`item-outcome-${ir.item_index}`}
                  >
                    <span className="font-['Manrope'] text-[#36454F] truncate">{ir.account_name || `Item ${ir.item_index + 1}`}</span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded font-['Manrope'] flex-shrink-0 ${
                        ir.auto_resolve
                          ? 'bg-[#4CAF50]/15 text-[#4CAF50]'
                          : 'bg-[#D9534F]/10 text-[#D9534F]'
                      }`}
                    >
                      {ir.label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Testimony prompt (if any good result) */}
            {showTestimony && (
              <TestimonyShareInline
                resolvedCount={r.resolved_count ?? (r.auto_resolved ? items.length : 0)}
                bureau={dispute?.bureau}
                onDismiss={() => setTestimonyDone(true)}
                onShared={() => setTestimonyDone(true)}
              />
            )}

            {/* Next steps list */}
            {(r.next_steps?.length ?? 0) > 0 && (
              <div>
                <h4 className="font-['Manrope'] font-semibold text-[#36454F] mb-2 text-sm">
                  {showDocsChecklist ? 'Typical documents you may need to send:' : 'Suggested next steps:'}
                </h4>
                <ul className="space-y-2">
                  {r.next_steps.map((s, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-[#36454F]/80 font-['Manrope']"
                      data-testid={`next-step-${i}`}
                    >
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#B76E79] flex-shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 pt-2">
              {showMovAction && (
                <Button
                  onClick={handleGenerateMov}
                  disabled={movDownloading}
                  className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
                  data-testid="generate-mov-btn"
                >
                  <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {movDownloading ? 'Preparing…' : 'Generate MOV Letter'}
                </Button>
              )}
              {showCfpbAction && (
                <a
                  href="https://www.consumerfinance.gov/complaint/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="open-cfpb-link"
                >
                  <Button
                    variant="outline"
                    className="border-[#D9534F] text-[#D9534F] hover:bg-[#D9534F] hover:text-white font-['Manrope'] rounded-sm"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    File CFPB Complaint
                  </Button>
                </a>
              )}
              <Button
                variant="ghost"
                onClick={() => setStep('select')}
                className="text-[#36454F]/60 font-['Manrope'] ml-auto"
                data-testid="response-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} />
                Change answer
              </Button>
              <Button
                onClick={() => handleOpenChange(false)}
                className="bg-[#36454F] hover:bg-[#36454F]/90 text-white font-['Manrope'] rounded-sm"
                data-testid="response-done-btn"
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SingleItemSelect({ options, selected, onSelect, notes, onNotes }) {
  return (
    <div className="space-y-3">
      {options.map((opt) => {
        const Icon = opt.icon;
        const isSelected = selected === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onSelect(opt.id)}
            className={`w-full text-left p-4 rounded-sm border transition-all ${TONE[opt.tone]} ${
              isSelected ? 'ring-2 ring-[#B76E79]' : ''
            }`}
            data-testid={`response-option-${opt.id}`}
          >
            <div className="flex items-start gap-3">
              <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${TONE_ICON[opt.tone]}`} strokeWidth={1.5} />
              <div>
                <p className="font-['Manrope'] font-medium text-[#36454F]">{opt.label}</p>
                <p className="text-sm text-[#36454F]/60 font-['Manrope'] mt-0.5">{opt.description}</p>
              </div>
            </div>
          </button>
        );
      })}

      <div className="pt-2">
        <label className="text-xs font-['Manrope'] text-[#36454F]/65 block mb-1">
          Notes (optional) — what did the letter say?
        </label>
        <Textarea
          value={notes}
          onChange={(e) => onNotes(e.target.value)}
          placeholder="E.g., 'Item verified with original creditor, no documentation provided'"
          className="border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope'] text-sm min-h-[60px]"
          data-testid="response-notes-input"
        />
      </div>
    </div>
  );
}

function PerItemSelect({ items, perItem, onChange, notes, onNotes }) {
  return (
    <div className="space-y-3">
      {items.map((item, idx) => {
        const value = perItem[idx] || '';
        const selectedOpt = PER_ITEM_OPTIONS.find((o) => o.id === value);
        return (
          <div
            key={idx}
            className={`p-3 border rounded-sm transition-all ${
              value ? 'border-[#B76E79]/40 bg-[#E0BFB8]/5' : 'border-[#36454F]/10 bg-[#F5F5F5]'
            }`}
            data-testid={`per-item-row-${idx}`}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <p className="font-['Manrope'] font-medium text-[#36454F] text-sm truncate">
                  {item.account_name || `Item ${idx + 1}`}
                </p>
                {item.account_number && (
                  <p className="text-xs text-[#36454F]/50 font-['Manrope'] truncate">
                    {item.account_number}
                  </p>
                )}
              </div>
              {item.issue_type && (
                <span className="text-[10px] px-2 py-0.5 bg-[#D9534F]/10 text-[#D9534F] rounded font-['Manrope'] flex-shrink-0">
                  {item.issue_type}
                </span>
              )}
            </div>

            <Select value={value} onValueChange={(v) => onChange(idx, v)}>
              <SelectTrigger
                className="border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope'] text-sm h-9"
                data-testid={`per-item-select-${idx}`}
              >
                <SelectValue placeholder="What did the bureau do with this item?" />
              </SelectTrigger>
              <SelectContent className="z-[9999] bg-white">
                {PER_ITEM_OPTIONS.map((o) => (
                  <SelectItem
                    key={o.id}
                    value={o.id}
                    className="font-['Manrope'] text-sm py-2 cursor-pointer"
                    data-testid={`per-item-option-${idx}-${o.id}`}
                  >
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedOpt && (
              <p className="text-[11px] text-[#36454F]/55 font-['Manrope'] mt-1.5">
                {selectedOpt.description}
              </p>
            )}
          </div>
        );
      })}

      <div className="pt-1">
        <label className="text-xs font-['Manrope'] text-[#36454F]/65 block mb-1">
          Notes (optional)
        </label>
        <Textarea
          value={notes}
          onChange={(e) => onNotes(e.target.value)}
          placeholder="Anything else from the bureau's letter?"
          className="border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope'] text-sm min-h-[50px]"
          data-testid="response-notes-input"
        />
      </div>
    </div>
  );
}
